import { ROOTS, EVENT_ROUTES, SETTINGS_ROUTES } from "@app/config/constants";
import { TFunction } from "i18next";

import {
  HomeIcon,
  CalendarIcon,
  PersonIcon,
  TeamIcon,
  CreditCardIcon,
  GlobeIcon,
  LocationIcon,
  ProfileIcon,
  OrganizationIcon,
} from "@medentic/polaris-icons";

const EVENTS_LIST_DEFAULT_PARAMS: Record<string, string> = {
  tab: "upcoming",
  sort: "starting desc",
};

const withQueryParams = (path: string, params: Record<string, string>) => {
  const query = new URLSearchParams(params).toString();
  return query ? `${path}?${query}` : path;
};

interface EventNavigationItem {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  url: string;
  selected: boolean;
  accessibilityLabel?: string;
  subNavigationItems?: EventNavigationItem[];
}

export const getEventNavigationItems = (
  t: TFunction,
  eventOrganizerId: string,
  locationPathname: string
): EventNavigationItem[] => [
  {
    label: t("eventNavigation.dashboard"),
    icon: HomeIcon,
    url: `${ROOTS.EVENTS}/${eventOrganizerId}`,
    selected:
      locationPathname === `${ROOTS.EVENTS}/${eventOrganizerId}` ||
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.DASHBOARD}`,
  },
  {
    label: t("eventNavigation.events"),
    icon: CalendarIcon,
    url: withQueryParams(
      `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.EVENTS}`,
      EVENTS_LIST_DEFAULT_PARAMS
    ),
    selected:
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.EVENTS}` ||
      locationPathname.startsWith(
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.EVENTS}/`
      ),
  },
  {
    label: t("eventNavigation.attendees"),
    icon: TeamIcon,
    url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.ATTENDEES}`,
    selected:
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.ATTENDEES}` ||
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.ATTENDEE_TAGS}` ||
      locationPathname.startsWith(
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.ATTENDEES}/`
      ),
    subNavigationItems: [
      {
        label: t("eventNavigation.attendeeTags"),
        icon: "",
        url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.ATTENDEE_TAGS}`,
        selected:
          locationPathname ===
          `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.ATTENDEE_TAGS}`,
      },
    ],
  },
  {
    label: t("eventNavigation.instructors"),
    icon: PersonIcon,
    url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.INSTRUCTORS}`,
    selected:
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.INSTRUCTORS}` ||
      locationPathname.startsWith(
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.INSTRUCTORS}/`
      ),
  },
  {
    label: t("eventNavigation.finance"),
    icon: CreditCardIcon,
    url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.PAYOUTS}`,
    selected:
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.PAYOUTS}` ||
      locationPathname.startsWith(
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.PAYOUTS}/`
      ) ||
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.TRANSACTIONS}` ||
      locationPathname.startsWith(
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.TRANSACTIONS}/`
      ),
    subNavigationItems: [
      {
        label: t("eventNavigation.payouts"),
        icon: "",
        url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.PAYOUTS}`,
        selected:
          locationPathname ===
            `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.PAYOUTS}` ||
          locationPathname.startsWith(
            `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.PAYOUTS}/`
          ),
      },
      {
        label: t("eventNavigation.transactions"),
        icon: "",
        url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.TRANSACTIONS}`,
        selected:
          locationPathname ===
            `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.TRANSACTIONS}` ||
          locationPathname.startsWith(
            `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.TRANSACTIONS}/`
          ),
      },
    ],
  },
  {
    label: t("eventNavigation.brands"),
    icon: GlobeIcon,
    url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.BRANDS}`,
    selected:
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.BRANDS}` ||
      locationPathname.startsWith(
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.BRANDS}/`
      ),
  },
  {
    label: t("eventNavigation.venues"),
    icon: LocationIcon,
    url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.VENUES}`,
    selected:
      locationPathname ===
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.VENUES}` ||
      locationPathname.startsWith(
        `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.VENUES}/`
      ),
  },
  {
    label: t("eventNavigation.myProfile"),
    icon: ProfileIcon,
    url: `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.PROFILE}`,
    selected:
      locationPathname ===
      `${ROOTS.EVENTS}/${eventOrganizerId}/${EVENT_ROUTES.PROFILE}`,
  },
];

export const getEventSettingsNavigationItems = (
  t: TFunction,
  selectedEventOrganizerId: string,
  locationPathname: string
): EventNavigationItem[] => {
  const SETTING_BASE = `${ROOTS.EVENTS}/${selectedEventOrganizerId}/${EVENT_ROUTES.SETTINGS}`;
  return [
    {
      label: t("settingsNavigation.details"),
      icon: OrganizationIcon,
      url: `${SETTING_BASE}/${SETTINGS_ROUTES.DETAILS}`,
      selected: locationPathname.includes(
        `${SETTING_BASE}/${SETTINGS_ROUTES.DETAILS}`
      ),
    },
    {
      label: t("settingsNavigation.members-permissions"),
      icon: TeamIcon,
      url: `${SETTING_BASE}/${SETTINGS_ROUTES.MEMBERS_PERMISSIONS}`,
      selected: locationPathname.includes(
        `${SETTING_BASE}/${SETTINGS_ROUTES.MEMBERS_PERMISSIONS}`
      ),
    },
    {
      label: t("settingsNavigation.payouts"),
      icon: CreditCardIcon,
      url: `${SETTING_BASE}/${SETTINGS_ROUTES.PAYOUTS}`,
      selected: locationPathname.includes(
        `${SETTING_BASE}/${SETTINGS_ROUTES.PAYOUTS}`
      ),
    },
  ];
};
